import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { LoginResponseData } from '@EHSNavigator/backend';
import { AuthApi } from '.';
import Axios from '../Axios';
import { AppDispatch } from '../../store/store';

import { getSession, logout } from '../../actions/useAction';
import { useLocalStorage } from '@uidotdev/usehooks';

import * as Sentry from '@sentry/react';
import { captureError } from '../../utils/sentry';

const useAuthenticatedAxios = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const [user, setUser] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (import.meta.env.PROD && dsn && user) {
      Sentry.setUser({
        ...user,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    const requestInterceptor = Axios.interceptors.request.use(
      (config) => {
        if (!user) return config;
        config.headers.Authorization = `Bearer ${user.accessToken}`;
        return config;
      },
      (error) => {
        captureError(error);
        return Promise.reject(error);
      },
    );

    const responseInterceptor = Axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        captureError(error);

        if (!user) return Promise.reject(error);

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          setIsRefreshing(true);

          try {
            const {
              data: { valid },
            } = await AuthApi.authControllerCheckSessionValidity({
              refreshTokenDTO: { refresh: user.refresh },
            });

            if (valid) {
              const res = await AuthApi.authControllerRefreshToken({
                refreshTokenDTO: { refresh: user.refresh },
              });

              const { accessToken } = res.data;
              setUser({ ...user, accessToken });

              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return Axios(originalRequest);
            } else {
              dispatch(logout(setUser));
            }
          } catch (refreshError) {
            captureError(refreshError);
            dispatch(logout(setUser));
          }
        }

        if (error?.response?.data?.statusCode === 404) {
          navigate('/404');
        }

        return Promise.reject(error);
      },
    );

    return () => {
      Axios.interceptors.request.eject(requestInterceptor);
      Axios.interceptors.response.eject(responseInterceptor);
    };
  }, [user?.accessToken, user, user?.refresh]);

  useEffect(() => {
    if (isRefreshing) {
      dispatch(getSession(user));
      setIsRefreshing(false);
    }
  }, [isRefreshing, user]);

  return Axios;
};

export default useAuthenticatedAxios;
